/* eslint-disable max-statements */

import { Button, Group, LoadingOverlay } from '@mantine/core';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { TSDListItem } from '@e-origin/contracts';
import { CustomsStateNotificationType, TSDCriteria, TSDStatus, TSDType } from '@e-origin/shared';

import { ListFilterChangeEvent, ListFilterDropdown, ListFilterDropdownElements, SearchBox, Table } from '@components';
import { ContentLayout } from '@features/layout';
import { getTSDInitialCriteria, useTSDs } from '@hooks/tsd';
import { removeUndefined, STORAGE_KEYS } from '@utils';
import { setStoredCriteria } from '../../../criteria.caching';
import { TSDModalManager } from './TSDModalManager';
import { TSDTypeView } from './TSDTypeView';
import { useTSDTableColumns } from './useTSDTableColumns';

export const TSDList = () => {
  const criteria = getTSDInitialCriteria();
  const storeCriteria = () => {
    setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_TSD_CRITERIA, { filters, sorting, pagination });
  };

  const [filters, setFilters] = useState<TSDCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<TSDCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<TSDCriteria['pagination']>(criteria.pagination);

  const { data: { list: tsdList, total: totalTSDs } = {}, isLoading } = useTSDs({ filters, sorting, pagination });

  const [tsdView, setTSDView] = useState(criteria.filters.type);

  const statusesOptions = useMemo<ListFilterDropdownElements>(() => {
    return {
      [TSDStatus.NOT_SENT as string]: {
        label: 'Not sent',
        checked: filters.status?.includes(TSDStatus.NOT_SENT) ?? false,
      },
      [TSDStatus.SENT as string]: {
        label: 'Sent',
        checked: filters.status?.includes(TSDStatus.SENT) ?? false,
      },
      [TSDStatus.REJECTED as string]: {
        label: 'Rejected',
        checked: filters.status?.includes(TSDStatus.REJECTED) ?? false,
      },
      [TSDStatus.PRELODGED as string]: {
        label: 'Pre-lodged',
        checked: filters.status?.includes(TSDStatus.PRELODGED) ?? false,
      },
      [TSDStatus.PRESENTED as string]: {
        label: 'Presented',
        checked: filters.status?.includes(TSDStatus.PRESENTED) ?? false,
      },
      [TSDStatus.LINKED as string]: {
        label: 'Linked',
        checked: filters.status?.includes(TSDStatus.LINKED) ?? false,
      },
      [TSDStatus.NOT_LINKED as string]: {
        label: 'Not linked',
        checked: filters.status?.includes(TSDStatus.NOT_LINKED) ?? false,
      },
      [TSDStatus.ACCEPTED as string]: {
        label: 'Accepted',
        checked: filters.status?.includes(TSDStatus.ACCEPTED) ?? false,
      },
      [TSDStatus.CLEARED as string]: {
        label: 'Cleared',
        checked: filters.status?.includes(TSDStatus.CLEARED) ?? false,
      },
      [TSDStatus.INVALIDATED as string]: {
        label: 'Invalidated',
        checked: filters.status?.includes(TSDStatus.INVALIDATED) ?? false,
      },
      [TSDStatus.UNDER_CONTROL as string]: {
        label: 'Under control',
        checked: filters.status?.includes(TSDStatus.UNDER_CONTROL) ?? false,
      },
      [TSDStatus.IRREGULARITY_UNDER_INVESTIGATION as string]: {
        label: 'Irregularity under investigation',
        checked: filters.status?.includes(TSDStatus.IRREGULARITY_UNDER_INVESTIGATION) ?? false,
      },
      [TSDStatus.MEASURES_REQUIRED as string]: {
        label: 'Measures required',
        checked: filters.status?.includes(TSDStatus.MEASURES_REQUIRED) ?? false,
      },
      [TSDStatus.TEMPORARY_STORAGE_ENDED as string]: {
        label: 'Temporary storage ended',
        checked: filters.status?.includes(TSDStatus.TEMPORARY_STORAGE_ENDED) ?? false,
      },
    };
  }, [filters.status]);

  const notificationsDropdownOptions = useMemo<ListFilterDropdownElements>(() => {
    return {
      [CustomsStateNotificationType.NOTIFICATION_PENDING]: {
        label: 'Notifications pending',
        checked: filters.filterByNotifications?.includes(CustomsStateNotificationType.NOTIFICATION_PENDING) ?? false,
      },
    };
  }, [filters.filterByNotifications]);

  const filterByNotificationsHandler = (notificationOption) => {
    const { id, checked } = notificationOption;

    if (!id) return;

    const current = filters.filterByNotifications || [];

    const updated = checked ? [...current, id] : current.filter((item) => item !== id);

    setNewFilter('filterByNotifications', updated);
  };

  const columns = useTSDTableColumns({ pagination, tsdView });

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const setNewFilter = (field: string, value: string | string[]) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...{ [field]: value },
    }) as TSDCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ page: 1, size: pagination.size });
  };

  useEffect(() => {
    storeCriteria();
  }, [pagination, sorting, filters]);

  const handleTSDType = (value: TSDType) => {
    setTSDView(value);
    setNewFilter('type', value);
  };

  const searchTSDs = (searchQuery: string) => {
    setNewFilter('searchQuery', searchQuery);
  };

  const toggleNewTSDModal = () => {
    TSDModalManager.openCreateTSD(tsdView);
  };

  const filterByStatus = (statusOption: ListFilterChangeEvent) => {
    const statusesOptionsCopy = cloneDeep(statusesOptions);
    if (!statusOption.id) {
      Object.keys(statusesOptionsCopy).forEach((option) => {
        statusesOptionsCopy[option].checked = statusOption.checked;
      });
    } else {
      statusesOptionsCopy[statusOption.id as TSDStatus].checked = statusOption.checked;
    }

    const selectedStatuses = Object.keys(statusesOptionsCopy)
      .filter((option) => statusesOptionsCopy[option].checked)
      .map((option) => option);

    if (selectedStatuses.length) {
      setNewFilter('status', selectedStatuses);
    } else {
      const { status, ...restFilters } = filters;
      setFilters(restFilters);
    }
  };

  if (!tsdList) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Temporary Storage Declarations</ContentLayout.Title>
        <ContentLayout.Filters>
          <TSDTypeView value={tsdView} onChange={handleTSDType} />
          <Button data-pw="new-temporary-storage-btn" onClick={toggleNewTSDModal} variant="outline" size="md" h={55}>
            New temporary storage
          </Button>
          <SearchBox
            handleSearch={searchTSDs}
            value={filters?.searchQuery}
            placeholder="Search by Name, LRN, CRN or MRN"
            data-pw="temporary-storage-search-input"
          />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="flex-end" mb="lg">
          <ListFilterDropdown title={'Notifications'} values={notificationsDropdownOptions} onChange={filterByNotificationsHandler} hideShowAll />
          <ListFilterDropdown title={'Status filter'} values={statusesOptions} onChange={filterByStatus} />
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table<TSDListItem>
          columns={columns}
          dataSource={tsdList || []}
          total={totalTSDs}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
